/** Phase E — downline policy (70% + permission, retained spread), authority status and payout readiness. */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { StatusPill, btnPrimary, btnSmall, errorText, inputCls } from '../finance/financeUi';

const COMPONENTS = { submerchant: ['ACTIVE', 'PENDING', 'NONE', 'SUSPENDED'], funding_bank: ['VERIFIED', 'PENDING', 'NONE', 'FAILED'], ach_origination: ['ACTIVE', 'PENDING', 'NONE', 'SUSPENDED'] };

function Policy({ tenantId }) {
  const [p, setP] = useState(null);
  useEffect(() => { if (tenantId) api.request(`/payer-runs/policy?tenant_id=${tenantId}`).then(setP).catch(() => setP(null)); }, [tenantId]);
  if (!p) return null;
  const save = async () => {
    try { setP(await api.request(`/payer-runs/policy?tenant_id=${tenantId}`, { method: 'PUT', body: JSON.stringify({ min_effective_share: Number(p.min_effective_share), minimum_retained_spread: Number(p.minimum_retained_spread), minimum_msc_retained_margin: Number(p.minimum_msc_retained_margin) }) })); toast.success('Downline policy saved'); } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <div className="flex flex-wrap items-end gap-3 px-5 py-4 text-sm" data-testid="downline-policy">
      <label><span className="mb-1 block text-xs text-muted-foreground">Min. effective share to build a downline (%)</span>
        <input type="number" className={`${inputCls} w-28`} value={p.min_effective_share} onChange={(e) => setP({ ...p, min_effective_share: e.target.value })} data-testid="policy-min-share" /></label>
      <label><span className="mb-1 block text-xs text-muted-foreground">Min. retained spread (points)</span>
        <input type="number" className={`${inputCls} w-28`} value={p.minimum_retained_spread} onChange={(e) => setP({ ...p, minimum_retained_spread: e.target.value })} data-testid="policy-spread" /></label>
      <label><span className="mb-1 block text-xs text-muted-foreground">MSC min. retained margin (points)</span>
        <input type="number" className={`${inputCls} w-28`} value={p.minimum_msc_retained_margin} onChange={(e) => setP({ ...p, minimum_msc_retained_margin: e.target.value })} data-testid="policy-msc-margin" /></label>
      <button type="button" className={btnPrimary} onClick={save} data-testid="policy-save-btn">Save policy</button>
      <span className="text-xs text-muted-foreground">{p.source === 'DEFAULT' ? 'MSC default' : 'Tenant policy'}</span>
    </div>
  );
}

function Readiness({ row, onChange }) {
  const set = async (component, status) => {
    const note = window.prompt(`Verification note for ${component.replace('_', ' ')} → ${status}`);
    if (!note) return;
    try { await api.request(`/payer-runs/readiness/${row.user_id}`, { method: 'PUT', body: JSON.stringify({ component, status, note }) }); toast.success('Readiness recorded (manual verification)'); onChange(); } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <div className="flex flex-wrap gap-2">
      {Object.entries(COMPONENTS).map(([c, opts]) => (
        <select key={c} className="h-8 rounded-md border bg-background px-2 text-xs" value={row.readiness[c].status} onChange={(e) => set(c, e.target.value)} data-testid={`readiness-${c}-${row.user_id}`}>
          {opts.map((o) => <option key={o} value={o}>{c.replace('_', ' ')}: {o}</option>)}
        </select>
      ))}
    </div>
  );
}

function BasisActions({ row, onChange }) {
  if (row.role !== 'iso' || row.basis_status === 'CONFIGURED') return null;
  const set = async (mode) => {
    const ceiling = mode === 'CUSTOM_CEILING' ? window.prompt('Economic ceiling (% share)') : null;
    if (mode === 'CUSTOM_CEILING' && !ceiling) return;
    const reason = window.prompt('Audit reason'); if (!reason) return;
    try { await api.request(`/payer-runs/economic-basis/${row.user_id}`, { method: 'PUT', body: JSON.stringify({ mode, reason, ceiling_share: ceiling ? Number(ceiling) : null, effective_from: new Date().toISOString().slice(0, 10) }) }); toast.success('Economic basis recorded'); onChange(); } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <span className="flex gap-1">
      <button type="button" className={btnSmall} onClick={() => set('CUSTOM_CEILING')} data-testid={`basis-ceiling-${row.user_id}`}>Set ceiling</button>
      <button type="button" className={btnSmall} onClick={() => set('SPREAD_POLICY_EXEMPT')} data-testid={`basis-exempt-${row.user_id}`}>Exempt</button>
    </span>
  );
}

export function DownlineAuthority({ tenantId }) {
  const [rows, setRows] = useState([]);
  const load = () => tenantId && api.request(`/payer-runs/authority?tenant_id=${tenantId}`).then(setRows).catch((e) => toast.error(errorText(e)));
  useEffect(() => { load(); }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps
  const scan = async () => {
    try { const r = await api.request('/payer-runs/authority/scan', { method: 'POST', body: JSON.stringify({ tenant_id: tenantId }) }); toast.success(`${r.flagged.length} downline(s) restricted for review`); load(); } catch (e) { toast.error(errorText(e)); }
  };
  const review = async (uid, decision) => {
    const note = window.prompt('Review note'); if (!note) return;
    try { await api.request(`/payer-runs/authority/${uid}/review`, { method: 'POST', body: JSON.stringify({ decision, note }) }); toast.success('Review recorded'); load(); } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <div data-testid="downline-authority">
      <Policy tenantId={tenantId} />
      <div className="flex items-center justify-between border-t px-5 py-3">
        <p className="text-xs text-muted-foreground">Agents need the minimum share and MSC permission. Readiness is verified by hand for now and will come from the provider later.</p>
        <button type="button" className={btnSmall} onClick={scan} data-testid="authority-scan-btn">Check downlines against policy</button>
      </div>
      <ul className="divide-y border-t text-sm" data-testid="authority-list">
        {rows.map((a) => (
          <li key={a.user_id} className="space-y-2 px-5 py-3" data-testid={`authority-row-${a.user_id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-medium">{a.name} <span className="text-xs font-normal text-muted-foreground">· {a.role === 'iso' ? 'ISO' : 'Agent'} · share {a.effective_share ?? '—'}% · max offer {a.max_downstream_offer ?? '—'}% · {a.downline_count} in downline</span></span>
              <span className="flex items-center gap-2">
                <StatusPill value={a.status} testid={`authority-status-${a.user_id}`} />
                <StatusPill value={`BASIS_${a.basis_status}`} testid={`basis-status-${a.user_id}`} />
                <BasisActions row={a} onChange={load} />
                <StatusPill value={a.readiness.downline_payout_ready ? 'PAYOUT_READY' : 'NOT_PAYOUT_READY'} testid={`payout-ready-${a.user_id}`} />
                {a.status === 'GRANDFATHERED_RESTRICTED' && <><button type="button" className={btnSmall} onClick={() => review(a.user_id, 'RESTORE')} data-testid={`authority-restore-${a.user_id}`}>Restore</button><button type="button" className={btnSmall} onClick={() => review(a.user_id, 'KEEP_RESTRICTED')} data-testid={`authority-keep-${a.user_id}`}>Keep restricted</button></>}
              </span>
            </div>
            <Readiness row={a} onChange={load} />
          </li>
        ))}
      </ul>
    </div>
  );
}
